import React, { useState, useContext, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  FLICKER_INTERVAL_MS,
  FLICKER_TO_FINAL_DELAY_MS,
  FINAL_TO_BRAND_DELAY_MS,
  BRAND_TO_READY_DELAY_MS,
  READY_TO_FORM_DELAY_MS,
} from './loginAnimation';
import './Auth.css';

// Theater-related phrases for the flickering animation
const flickerPhrases = [
  'Your Stage.',
  'Your Craft.',
  'Your Work.',
  'Your People.',
  'Your Audition.',
  'Your Role.',
  'Your Moment.',
  'Your Story.',
  'Your Dream.',
  'Your Spotlight.',
];

// Rainbow stripe colors
const stripeColors = ['#5a407d', '#e32c74', '#f57b57', '#f29d40'];

const finalPhrase = 'Your Network. Your Spotlight.';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');

  // Animation states - starts immediately on page load
  const [currentPhrase, setCurrentPhrase] = useState(flickerPhrases[0]);
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [phraseColor, setPhraseColor] = useState(stripeColors[0]);
  const [animationPhase, setAnimationPhase] = useState('flickering'); // flickering, final, brand, form

  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // SHO-38: api.js redirects expired sessions to `/login?reason=session-expired`.
  // Capture the reason once on mount, then strip the query so a refresh or
  // bookmark of this page doesn't keep showing a stale banner.
  useEffect(() => {
    if (searchParams.get('reason') === 'session-expired') {
      setInfoMessage('Your session expired, please sign in again.');
      const next = new URLSearchParams(searchParams);
      next.delete('reason');
      setSearchParams(next, { replace: true });
    }
    // Run once on mount; reading the param later would re-trigger after we clear it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Animation runs immediately on mount
  useEffect(() => {
    if (animationPhase === 'flickering' && phraseIndex < flickerPhrases.length) {
      const timeout = setTimeout(() => {
        const nextIndex = phraseIndex + 1;
        setPhraseIndex(nextIndex);
        if (nextIndex < flickerPhrases.length) {
          setCurrentPhrase(flickerPhrases[nextIndex]);
          setPhraseColor(stripeColors[nextIndex % stripeColors.length]);
        }
      }, FLICKER_INTERVAL_MS);
      return () => clearTimeout(timeout);
    }

    if (animationPhase === 'flickering' && phraseIndex >= flickerPhrases.length) {
      // Move to final phrase
      setTimeout(() => {
        setCurrentPhrase(finalPhrase);
        setPhraseColor(stripeColors[0]);
        setAnimationPhase('final');
      }, FLICKER_TO_FINAL_DELAY_MS);
    }
  }, [phraseIndex, animationPhase]);

  useEffect(() => {
    if (animationPhase === 'final') {
      setTimeout(() => setAnimationPhase('brand'), FINAL_TO_BRAND_DELAY_MS);
    } else if (animationPhase === 'brand') {
      setTimeout(() => setAnimationPhase('ready'), BRAND_TO_READY_DELAY_MS);
    } else if (animationPhase === 'ready') {
      setTimeout(() => setAnimationPhase('form'), READY_TO_FORM_DELAY_MS);
    }
  }, [animationPhase]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    // Once the user is actively signing in, the prior session-expired
    // banner is no longer the relevant signal — clear it so any new error
    // takes its place cleanly.
    setInfoMessage('');
    setLoading(true);

    const result = await login({ email, password });

    if (!result.success) {
      setError(result.error);
      setLoading(false);
      return;
    }

    // SHO-40: route to onboarding if the account never finished setup so the
    // user resumes where they left off instead of getting trapped on the
    // login page (PublicRoute keeps Login mounted while onboarding_complete
    // is false, which would otherwise hang the "Signing in..." spinner).
    if (result.user?.onboarding_complete === false) {
      navigate('/onboarding');
    } else {
      navigate('/feed');
    }
  };

  return (
    <div className="auth-page" style={{ backgroundColor: phraseColor }}>
      {/* Brand + Tagline Container */}
      <div className={`auth-hero ${animationPhase === 'form' ? 'fly-up' : ''}`}>
        {/* Brand - appears after flickering */}
        <div
          className={`auth-brand ${animationPhase === 'brand' || animationPhase === 'ready' || animationPhase === 'form' ? 'visible' : ''}`}
        >
          Showcase
        </div>

        {/* Flickering Phrase */}
        <div className={`auth-phrase ${animationPhase === 'flickering' ? 'flickering' : 'final'}`}>
          {currentPhrase}
        </div>
      </div>

      {/* Login Form - slides up after animation */}
      <div className={`auth-form-container ${animationPhase === 'form' ? 'visible' : ''}`}>
        <div className="auth-form-card">
          {infoMessage && (
            <div className="auth-info" role="status">
              {infoMessage}
            </div>
          )}
          {error && <div className="auth-error">{error}</div>}

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="auth-field">
              <input
                type="email"
                className="auth-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                aria-label="Email"
                required
              />
            </div>
            <div className="auth-field">
              <input
                type="password"
                className="auth-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                aria-label="Password"
                required
              />
            </div>
            <button type="submit" className="auth-submit-btn" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="auth-footer">
            New to Showcase? <Link to="/register">Create Account</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
